/**
 * 屏幕框选工具插件 —— 实现 ActivatableToolPlugin 接口。
 * 激活后通过拖拽绘制屏幕矩形，调用 PickingService.pickRect 执行区域拾取，
 * 将结果设置到 SelectionService。
 */

import * as THREE from 'three'
import { WEBGL2_ONLY_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { prefersAsyncToolPicking } from '@/plugins/tools/shared/toolPickPolicy'
import {
  getActiveView,
  getActiveViewCamera,
  getOptionalViewRegistry,
  isActiveViewFrame,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import { getLoggerManager } from '@/shared/logging'
import type {
  InputConsumer,
  NormalizedKeyEvent,
  NormalizedPointerEvent,
  RectPickQuery,
} from '@/shared/types/core'
import type { ActivatableToolPlugin, ActivationToken } from '@/shared/types/plugin'
import type {
  InputRouter,
  PickingService,
  SelectionService,
  ViewEventRouter,
  ViewInputConsumer,
  ViewKeyEvent,
  ViewPointerEvent,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { screenToViewNDC } from '@/shared/utils/index'

const log = getLoggerManager().getLogger('tools')

/** 屏幕坐标点 */
export interface ScreenPoint {
  /** X 坐标，单位像素 */
  x: number
  /** Y 坐标，单位像素 */
  y: number
}

/** 框选矩形（屏幕坐标） */
export interface SelectRect {
  /** 起点，单位像素 */
  start: ScreenPoint
  /** 终点，单位像素 */
  end: ScreenPoint
}

type ScreenRect = {
  x: number
  y: number
  width: number
  height: number
}

/** 最小拖拽阈值（像素），低于此值视为误触 */
const MIN_DRAG_SIZE = 5

/**
 * 屏幕框选工具插件，用于拉框选择点云/场景对象。
 */
export class ScreenBoxSelectPlugin implements ActivatableToolPlugin {
  /** 插件名称 */
  readonly name = 'screenBoxSelect'
  readonly graphicsRequirements = WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 50
  /** 所属互斥组 */
  readonly group = 'default'
  /** 依赖的服务名列表 */
  readonly dependencies = ['PickingService', 'InputRouter', 'SelectionService'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['ScreenBoxSelectService'] as const

  /** 当前是否激活 */
  private _active = false
  /** 激活前的指针样式，停用时恢复 */
  private cursorBeforeActivate: string | null = null

  /** 由 PluginManager 注入的合法 token */
  private activationToken: ActivationToken | null = null

  /** 引擎上下文 */
  private context!: EngineContext

  /** 输入路由服务 */
  private inputRouter!: InputRouter

  /** 拾取服务 */
  private pickingService!: PickingService

  /** 选择服务 */
  private selectionService!: SelectionService

  /** 可选的多视口注册表 */
  private viewRegistry: ViewRegistry | null = null

  /** 可选的 view-local 输入路由 */
  private viewEventRouter: ViewEventRouter | null = null

  /** 当前是否通过 ViewEventRouter 注册输入 */
  private usingViewEventRouter = false

  /** 是否正在拖拽 */
  private isDragging = false
  /** 框选异步拾取令牌：新的框选/取消会让旧结果失效。 */
  private rectPickToken = 0

  /** 拖拽起始屏幕坐标 */
  private startScreen: ScreenPoint | null = null

  /** 拖拽当前屏幕坐标 */
  private endScreen: ScreenPoint | null = null

  /** 当前拖拽使用的屏幕坐标系矩形，与 start/endScreen 同坐标系 */
  private dragViewportRect: ScreenRect | null = null

  /** 当前拖拽传给拾取服务的 view-local 矩形 */
  private dragQueryViewRect: ScreenRect | null = null

  /** 当前拖拽命中的 view id */
  private dragViewId: string | null = null

  /** 当前拖拽使用的相机 */
  private dragCamera: THREE.Camera | null = null

  /** 指针宿主：走 GraphicsBackend 的 surface canvas，不读 legacy renderer。 */
  private cursorHost(): HTMLElement | null {
    return this.context?.graphics?.canvas ?? null
  }

  /** 缓存当前帧相机引用，供输入回调使用 */
  private currentCamera: THREE.Camera | null = null

  /** 当前 active view 标识 */
  private currentViewId: string | null = null

  /** 当前 active view 的屏幕矩形 */
  private currentViewRect: {
    x: number
    y: number
    width: number
    height: number
  } | null = null

  /** 缓存当前视口尺寸 */
  private viewportWidth = 0
  private viewportHeight = 0

  private readonly handleViewActivated = (event: {
    viewId: string
    previousViewId: string | null
  }) => {
    this.syncActiveViewState()
    if (event.previousViewId && event.previousViewId !== event.viewId) {
      this.resetDrag()
    }
  }

  /** 输入消费者实例 */
  private readonly consumer: InputConsumer = {
    name: 'screenBoxSelect',
    priority: 100,

    onPointerDown: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerDown(event)
    },

    onPointerMove: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerMove(event)
    },

    onPointerUp: (event: NormalizedPointerEvent): boolean => {
      return this.handlePointerUp(event)
    },

    onKeyDown: (event: NormalizedKeyEvent): boolean => {
      return this.handleKeyDown(event)
    },
  }

  /** ViewEventRouter 消费者，event.screen 已转换为容器局部坐标。 */
  private readonly viewConsumer: ViewInputConsumer = {
    name: 'screenBoxSelect',
    priority: 100,

    onPointerDown: (event: ViewPointerEvent): boolean => {
      return this.handlePointerDown(event)
    },

    onPointerMove: (event: ViewPointerEvent): boolean => {
      return this.handlePointerMove(event)
    },

    onPointerUp: (event: ViewPointerEvent): boolean => {
      return this.handlePointerUp(event)
    },

    onKeyDown: (event: ViewKeyEvent): boolean => {
      return this.handleKeyDown(event)
    },
  }

  /** 当前是否激活 */
  get isActive(): boolean {
    return this._active
  }

  /**
   * 获取当前框选矩形（供 Overlay 读取）。
   *
   * @returns 当前拖拽形成的矩形；未拖拽返回 null
   */
  getCurrentRect(): SelectRect | null {
    if (!this.isDragging || !this.startScreen || !this.endScreen) {
      return null
    }
    return {
      start: { ...this.startScreen },
      end: { ...this.endScreen },
    }
  }

  // ==================== 生命周期 ====================

  /**
   * 由 PluginManager 注入合法 token。
   *
   * @param token - 激活令牌
   */
  setActivationToken(token: ActivationToken): void {
    this.activationToken = token
  }

  /**
   * 初始化：存储引擎上下文，注册服务。
   *
   * @param context - 引擎上下文，用于服务注册与事件订阅
   */
  onInit(context: EngineContext): void {
    this.context = context
    context.registerService('ScreenBoxSelectService', this)
  }

  /** 启动：从上下文获取所需服务 */
  onStart(): void {
    this.inputRouter = this.context.getService('InputRouter')
    this.pickingService = this.context.getService('PickingService')
    this.selectionService = this.context.getService('SelectionService')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.viewEventRouter = this.context.hasService('ViewEventRouter')
      ? this.context.getService('ViewEventRouter')
      : null
    this.syncActiveViewState()
    if (this.viewRegistry) {
      this.context.events.on('view.activated', this.handleViewActivated)
    }
  }

  /**
   * 渲染视图前缓存当前相机和视口尺寸。
   *
   * @param frame - 视图帧上下文，含相机与视口信息
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!isActiveViewFrame(this.viewRegistry, frame)) return

    this.currentCamera = this.resolveActiveViewCamera() ?? frame.camera
    this.syncActiveViewState()
    this.viewportWidth = frame.viewport.width
    this.viewportHeight = frame.viewport.height
  }

  /** 销毁：确保已反激活 */
  onDestroy(): void {
    if (this.viewRegistry) {
      this.context.events.off('view.activated', this.handleViewActivated)
    }
    if (this._active && this.activationToken) {
      this.deactivate(this.activationToken)
    }
    this.viewRegistry = null
    this.viewEventRouter = null
  }

  // ==================== 激活/反激活 ====================

  /**
   * 激活框选工具，注册输入消费者。
   *
   * @param token - 激活凭据，用于权限校验
   */
  activate(token: ActivationToken): void {
    this.validateToken(token)
    if (this._active) return
    this._active = true
    // 框选工具激活时切换指针样式：否则用户拿不到「工具已激活」的反馈
    const cursorHost = this.cursorHost()
    if (cursorHost) {
      this.cursorBeforeActivate = cursorHost.style.cursor
      cursorHost.style.cursor = 'crosshair'
    }
    this.syncActiveViewState()
    if (this.viewEventRouter) {
      this.viewEventRouter.addConsumer(this.viewConsumer)
      this.usingViewEventRouter = true
      return
    }

    this.inputRouter.addConsumer(this.consumer)
    this.usingViewEventRouter = false
  }

  /**
   * 反激活框选工具，移除输入消费者并重置状态。
   *
   * @param token - 激活凭据，用于权限校验
   */
  deactivate(token: ActivationToken): void {
    this.validateToken(token)
    if (!this._active) return
    this._active = false
    if (this.usingViewEventRouter) {
      this.viewEventRouter?.removeConsumer(this.viewConsumer)
    } else {
      this.inputRouter.removeConsumer(this.consumer)
    }
    this.usingViewEventRouter = false
    this.resetDrag()
    const cursorHost = this.cursorHost()
    if (cursorHost) {
      cursorHost.style.cursor = this.cursorBeforeActivate ?? ''
    }
    this.cursorBeforeActivate = null
    // 停用后迟到的异步框选结果不得再写选择集
    this.rectPickToken += 1
    this.currentCamera = null
    this.currentViewId = null
    this.currentViewRect = null
  }

  // ==================== 输入处理 ====================

  /** 处理指针按下事件 */
  private handlePointerDown(event: NormalizedPointerEvent | ViewPointerEvent): boolean {
    // 右键取消拖拽
    if (event.button === 2) {
      if (this.isDragging) {
        this.resetDrag()
        return true
      }
      return false
    }

    // 仅处理左键
    if (event.button !== 0) return false

    this.syncEventViewState(event)
    // 新的框选开始：作废上一次尚未回写的异步结果
    this.rectPickToken += 1
    this.isDragging = true
    this.startScreen = { x: event.screen.x, y: event.screen.y }
    this.endScreen = { x: event.screen.x, y: event.screen.y }
    this.dragViewportRect = this.resolveEventViewportRect(event)
    this.dragQueryViewRect = this.resolveEventQueryViewRect(event)
    this.dragViewId = this.resolveEventViewId(event)
    this.dragCamera = this.resolveEventCamera(event)
    return true
  }

  /** 处理指针移动事件 */
  private handlePointerMove(event: NormalizedPointerEvent | ViewPointerEvent): boolean {
    if (!this.isDragging) return false
    this.endScreen = { x: event.screen.x, y: event.screen.y }
    return true
  }

  /** 处理指针抬起事件 */
  private handlePointerUp(_event: NormalizedPointerEvent | ViewPointerEvent): boolean {
    if (!this.isDragging || !this.startScreen || !this.endScreen) {
      return false
    }

    const rectWidth = Math.abs(this.endScreen.x - this.startScreen.x)
    const rectHeight = Math.abs(this.endScreen.y - this.startScreen.y)

    // 拖拽区域过小视为误触，忽略
    if (rectWidth < MIN_DRAG_SIZE || rectHeight < MIN_DRAG_SIZE) {
      this.resetDrag()
      return true
    }

    this.performRectPick()
    this.resetDrag()
    return true
  }

  /** 处理键盘事件 */
  private handleKeyDown(event: NormalizedKeyEvent | ViewKeyEvent): boolean {
    if (event.key === 'Escape' && this.isDragging) {
      this.resetDrag()
      return true
    }
    return false
  }

  // ==================== 核心逻辑 ====================

  /** 执行区域拾取并设置选择结果 */
  private performRectPick(): void {
    const camera = this.dragCamera ?? this.currentCamera
    if (!camera || !this.startScreen || !this.endScreen) return

    const minScreen = {
      x: Math.min(this.startScreen.x, this.endScreen.x),
      y: Math.min(this.startScreen.y, this.endScreen.y),
    }
    const maxScreen = {
      x: Math.max(this.startScreen.x, this.endScreen.x),
      y: Math.max(this.startScreen.y, this.endScreen.y),
    }

    const viewportRect = this.dragViewportRect ?? this.currentViewRect
    if (!viewportRect && (this.viewportWidth === 0 || this.viewportHeight === 0)) {
      return
    }

    const ndcMin = viewportRect
      ? screenToViewNDC(new THREE.Vector2(minScreen.x, maxScreen.y), viewportRect)
      : new THREE.Vector2(
          (minScreen.x / this.viewportWidth) * 2 - 1,
          -(maxScreen.y / this.viewportHeight) * 2 + 1
        )
    const ndcMax = viewportRect
      ? screenToViewNDC(new THREE.Vector2(maxScreen.x, minScreen.y), viewportRect)
      : new THREE.Vector2(
          (maxScreen.x / this.viewportWidth) * 2 - 1,
          -(minScreen.y / this.viewportHeight) * 2 + 1
        )
    const queryViewRect = this.dragQueryViewRect ?? this.currentViewRect

    const query: RectPickQuery = {
      min: ndcMin,
      max: ndcMax,
      camera,
      viewId: this.dragViewId ?? this.currentViewId ?? undefined,
      viewRect: queryViewRect ? { ...queryViewRect } : undefined,
    }

    // WebGPU 没有同步的矩形 GPU 拾取（pickRectSync），同步入口会回退到 CPU 矩形拾取，
    // 大范围框选可能阻塞主线程数百毫秒甚至更久，因此改走异步矩形拾取 + 令牌防过期回写。
    if (prefersAsyncToolPicking(this.context.graphics)) {
      const token = this.rectPickToken
      void this.pickingService
        .pickRectAsync(query)
        .then((results) => {
          if (token !== this.rectPickToken) {
            return
          }
          if (results.length === 0) {
            log.debug(`[ScreenBoxSelectPlugin] pickRectAsync 返回空结果`)
          }
          this.selectionService.set(results)
        })
        .catch(() => {
          // 单次框选拾取失败按空结果处理
        })
      return
    }

    const results = this.pickingService.pickRect(query)
    if (results.length === 0) {
      log.debug(`[ScreenBoxSelectPlugin] pickRect 返回空结果`)
    }
    this.selectionService.set(results)
  }

  /**
   * 重置拖拽状态。
   *
   * 注意：这里**不**作废在途的框选拾取——异步拾取是在抬指时才发起的，重置紧跟其后，若在此作废
   * 会让刚发出的请求结果永远被丢弃。令牌只在「开始新的框选」与「停用工具」时推进。
   */
  private resetDrag(): void {
    this.isDragging = false
    this.startScreen = null
    this.endScreen = null
    this.dragViewportRect = null
    this.dragQueryViewRect = null
    this.dragViewId = null
    this.dragCamera = null
  }

  /** 校验 token 合法性 */
  private validateToken(token: ActivationToken): void {
    if (token !== this.activationToken) {
      throw new Error('ScreenBoxSelectPlugin: 非法的 ActivationToken，操作被拒绝')
    }
  }

  private syncActiveViewState(): void {
    const activeView = getActiveView(this.viewRegistry)
    this.currentViewId = activeView?.id ?? null
    this.currentViewRect = activeView
      ? {
          x: activeView.rect.x,
          y: activeView.rect.y,
          width: activeView.rect.width,
          height: activeView.rect.height,
        }
      : null
    this.currentCamera = this.resolveActiveViewCamera() ?? this.currentCamera
  }

  private resolveActiveViewCamera(): THREE.Camera | null {
    return getActiveViewCamera(this.viewRegistry)
  }

  private syncEventViewState(event: NormalizedPointerEvent | ViewPointerEvent): void {
    if (!this.isViewPointerEvent(event)) {
      return
    }

    this.currentViewId = event.viewId
    this.currentViewRect = { ...event.viewRect }
    this.currentCamera = this.resolveViewCamera(event.viewId) ?? this.currentCamera
  }

  private resolveEventViewportRect(
    event: NormalizedPointerEvent | ViewPointerEvent
  ): ScreenRect | null {
    if (this.isViewPointerEvent(event)) {
      return { ...event.viewRect }
    }

    return event.viewport ? { ...event.viewport } : this.currentViewRect
  }

  private resolveEventQueryViewRect(
    event: NormalizedPointerEvent | ViewPointerEvent
  ): ScreenRect | null {
    if (this.isViewPointerEvent(event)) {
      return { ...event.viewRect }
    }

    return this.currentViewRect
  }

  private resolveEventViewId(event: NormalizedPointerEvent | ViewPointerEvent): string | null {
    if (this.isViewPointerEvent(event)) {
      return event.viewId
    }

    return this.currentViewId
  }

  private resolveEventCamera(
    event: NormalizedPointerEvent | ViewPointerEvent
  ): THREE.Camera | null {
    const viewId = this.resolveEventViewId(event)
    return (viewId ? this.resolveViewCamera(viewId) : null) ?? this.currentCamera
  }

  private resolveViewCamera(viewId: string): THREE.Camera | null {
    const managedCamera = this.viewRegistry?.getExtension(viewId, 'viewportCamera') as
      | { getActiveCamera?: () => THREE.Camera }
      | undefined
    return managedCamera?.getActiveCamera?.() ?? null
  }

  private isViewPointerEvent(
    event: NormalizedPointerEvent | ViewPointerEvent
  ): event is ViewPointerEvent {
    return 'viewId' in event && 'viewRect' in event
  }
}

export default ScreenBoxSelectPlugin
